import {useMemo, useState} from "react"
import cx from "classnames"
import {CalendarOutlined, UserOutlined} from "@ant-design/icons"
import {Tooltip} from "antd"
import {useTranslation} from "react-i18next"
import moment from "moment"
import {compact, get, isEmpty} from "lodash"
import {OutcomeSelect} from "components/OutcomeSelect"
import {getFullName} from "helpers"
import {checkIsKeepSystemDateEvent} from "helpers/calendar"
import {Settings} from "types/settings"
import styles from "./CalendarEventsTable.module.css"
import {useAllCalendarEventTypes} from "hooks/useAllEventTypes"
import {useHistory} from "react-router-dom"
import {useWorkflowTriggerOutcomeItems} from "hooks/useWorkflowTriggerOutcomeItems"
import {useVisible} from "hooks"
import {ConfirmPopup} from "uiKit"

export default function CalendarEventItem(props: {
    event: any
    outcomes?: Settings.Outcome[]
    isCalendarEvent?: boolean
    outcomeValue: any
    handleUpdateOutcome: (event: any, outcomeId: number) => void
}) {
    const {event, isCalendarEvent, outcomes, outcomeValue, handleUpdateOutcome} = props
    const {t} = useTranslation(["calendarEvents", "common"])
    const {eventTypes} = useAllCalendarEventTypes()
    const history = useHistory()
    const {hasWorkflowTrigger} = useWorkflowTriggerOutcomeItems()
    const confirmPopup = useVisible()
    const [outcome, setOutcome] = useState(outcomeValue)
    const [pendingOutcome, setPendingOutcome] = useState<Settings.Outcome | null>(null)
    const startDateValue = isCalendarEvent ? event.start : event.start_at_utc
    const endDateValue = isCalendarEvent ? event.end : event.end_at_utc
    const startDate = startDateValue ? moment.utc(startDateValue).local().format("MMM D, YYYY h:mm A") : "-"
    const endDate = endDateValue ? moment.utc(endDateValue).local().format("MMM D, YYYY h:mm A") : "-"
    const department = get(event, "json_data.department.name") || "-"
    const eventType = get(event, "json_data.eventType") || "-"
    const eventTypeId = get(event, "json_data.eventTypeId")
    const eventOutcomes = useMemo(
        () =>
            eventTypes
                .find((type) => type.eventTypeId === eventTypeId)
                ?.outcomeGroups?.flatMap((group) => group.outcomes) || [],
        [eventTypes, eventTypeId]
    )
    const isSystemDateEvent = checkIsKeepSystemDateEvent(event.type)
    const invitedUsers = useMemo(
        () => (isCalendarEvent ? compact(event.invitedUsers) : []),
        [isCalendarEvent, event.invitedUsers]
    )
    const invitedUsersLabel = useMemo(
        () =>
            invitedUsers
                .map((user) => getFullName(user))
                .filter(Boolean)
                .join(", "),
        [invitedUsers]
    )

    const isPastEvent = useMemo(() => {
        if (!endDateValue) {
            return false
        }

        return moment.utc(endDateValue).local().isBefore(moment())
    }, [endDateValue])

    const handleClick = () => {
        if (!isSystemDateEvent) {
            history.push(`/calendar-event/detail?id=${event.id}`)
        }
    }

    const applyOutcomeChange = (value: Settings.Outcome | null) => {
        setOutcome(value)
        handleUpdateOutcome(event, value?.outcomeId)
    }

    const handleOutcomeChange = (value: Settings.Outcome | null) => {
        if (value?.outcomeId && hasWorkflowTrigger(value.outcomeId)) {
            setPendingOutcome(value)
            confirmPopup.open()
            return
        }
        applyOutcomeChange(value)
    }

    const handleConfirmOutcomeChange = () => {
        applyOutcomeChange(pendingOutcome)
        setPendingOutcome(null)
        confirmPopup.close()
    }

    const handleCancelOutcomeChange = () => {
        setPendingOutcome(null)
        confirmPopup.close()
    }

    return (
        <>
            <div
                className={cx(styles.card, {
                    [styles.cardClickable]: !isSystemDateEvent,
                    [styles.cardPast]: isPastEvent
                })}
                onClick={handleClick}>
                <div className={styles.cardThumbnail}>
                    <CalendarOutlined />
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.cardMeta}>
                        {eventType !== "-" ? eventType : t("calendarEvents.eventType")}
                        {department !== "-" && <span> &middot; {department}</span>}
                        {isPastEvent && <span className={styles.pastBadge}>{t("calendarEvents.past")}</span>}
                    </div>
                    <Tooltip title={event.title}>
                        <div className={styles.cardTitle}>{event.title || "-"}</div>
                    </Tooltip>
                    <div className={styles.cardSubtitle}>
                        <CalendarOutlined style={{fontSize: 11}} />
                        <span>{startDate}</span>
                    </div>
                    {isCalendarEvent && event.ownerFullName && (
                        <div className={styles.cardSubtitle}>
                            <UserOutlined style={{fontSize: 11}} />
                            <span>
                                {t("calendarEvents.owner")}: {event.ownerFullName}
                            </span>
                        </div>
                    )}
                    {isCalendarEvent && !isEmpty(invitedUsers) && (
                        <Tooltip title={invitedUsersLabel}>
                            <div className={styles.cardSubtitle}>
                                <UserOutlined style={{fontSize: 11}} />
                                <span>
                                    {t("calendarEvents.invitedUsers")}: {invitedUsersLabel}
                                </span>
                            </div>
                        </Tooltip>
                    )}
                </div>
                <div className={styles.cardInfo}>
                    <div className={styles.cardDateLabel}>{t("calendarEvents.endDate")}</div>
                    <div className={styles.cardDateRange}>{endDate}</div>
                </div>
                <div className={styles.cardOutcome} onClick={(e) => e.stopPropagation()}>
                    {!isSystemDateEvent && (
                        <OutcomeSelect
                            value={outcome}
                            outcomes={outcomes ?? eventOutcomes}
                            onChange={handleOutcomeChange}
                            placeholder={t("calendarEvents.selectOutcome")}
                        />
                    )}
                </div>
            </div>
            <ConfirmPopup
                isVisible={confirmPopup.isVisible}
                title={t("calendarEvents.workflowTriggerConfirmTitle")}
                description={t("calendarEvents.workflowTriggerConfirmDescription")}
                onClose={handleCancelOutcomeChange}
                onConfirm={handleConfirmOutcomeChange}
            />
        </>
    )
}
